'use client';

import { useState, useRef, useEffect, useCallback } from 'react';

import { SliderPaginationButtons } from './SliderPaginationButtons';
import { HalfPageSliderProps } from './types';

import { classNames } from '@/shared/lib/classNames/classNames';

export const HalfPageSlider: React.FC<HalfPageSliderProps> = ({
  leftContent,
  slides,
  className,
  slideHeight = 460,
  slideWidth = 470,
  slideGap = 19,
  overlayUrl,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [isTransitioningToFirst, setIsTransitioningToFirst] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [swipeExecuted, setSwipeExecuted] = useState(false);
  const [blockAllInteractions, setBlockAllInteractions] = useState(false);

  const duplicatedSlides = getDuplicatedSlides(slides);
  const isSingleSlide = slides.length === 1;

  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const sliderRef = useRef<HTMLDivElement>(null);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handlePrev = useCallback(() => {
    if (isAnimating || duplicatedSlides.length <= 1) return;

    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }

    setIsAnimating(true);
    setPreviousIndex(currentIndex);

    const newIndex =
      currentIndex === 0 ? duplicatedSlides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);

    if (currentIndex === 0 && newIndex === duplicatedSlides.length - 1) {
      setIsTransitioningToFirst(false);
    }

    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  }, [isAnimating, duplicatedSlides.length, currentIndex]);

  const handleNext = useCallback(() => {
    if (isAnimating || duplicatedSlides.length <= 1) return;

    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }

    setIsAnimating(true);
    setPreviousIndex(currentIndex);

    const newIndex =
      currentIndex === duplicatedSlides.length - 1 ? 0 : currentIndex + 1;

    if (currentIndex === duplicatedSlides.length - 1 && newIndex === 0) {
      setIsTransitioningToFirst(true);
    }

    setCurrentIndex(newIndex);

    setTimeout(() => {
      setIsAnimating(false);

      setTimeout(() => {
        setIsTransitioningToFirst(false);
      }, 50);
    }, 500);
  }, [isAnimating, duplicatedSlides.length, currentIndex]);

  const getSlidePosition = (index: number) => {
    const isVirtualPrevious =
      index === duplicatedSlides.length - 1 && currentIndex === 0;
    const isVirtualNext =
      index === 0 && currentIndex === duplicatedSlides.length - 1;

    let indexDiff = index - currentIndex;

    if (isVirtualPrevious) {
      indexDiff = -1;
    } else if (isVirtualNext) {
      indexDiff = 1;
    } else if (indexDiff > duplicatedSlides.length / 2) {
      indexDiff -= duplicatedSlides.length;
    } else if (indexDiff < -duplicatedSlides.length / 2) {
      indexDiff += duplicatedSlides.length;
    }

    if (indexDiff < -1) {
      return { display: 'none' };
    }
    if (indexDiff < 0) {
      return {
        left: '0px',
        opacity: 0,
        transform: 'scale(0.7) translateZ(-200px)',
      };
    } else {
      const shouldHide =
        isTransitioningToFirst && index === previousIndex && isAnimating;

      return {
        left: `${indexDiff * (slideWidth + slideGap)}px`,
        opacity: shouldHide ? 0 : 1,
      };
    }
  };

  const getSlideClasses = (index: number) => {
    const isVirtualPrevious =
      index === duplicatedSlides.length - 1 && currentIndex === 0;
    const isVirtualNext =
      index === 0 && currentIndex === duplicatedSlides.length - 1;

    let indexDiff = index - currentIndex;

    if (isVirtualPrevious) {
      indexDiff = -1;
    } else if (isVirtualNext) {
      indexDiff = 1;
    } else if (indexDiff > duplicatedSlides.length / 2) {
      indexDiff -= duplicatedSlides.length;
    } else if (indexDiff < -duplicatedSlides.length / 2) {
      indexDiff += duplicatedSlides.length;
    }

    const baseClasses =
      'h-full absolute top-0 transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] origin-center';

    if (isTransitioningToFirst && index === previousIndex) {
      return `${baseClasses} opacity-0 scale-100 translate-z-0 z-[1]`;
    }

    if (indexDiff === 0) {
      return `${baseClasses} opacity-100 scale-100 translate-z-0 z-[3]`;
    } else if (indexDiff === -1) {
      return `${baseClasses} opacity-0 scale-[0.7] -translate-z-[200px] z-[1] delay-[50ms]`;
    } else if (indexDiff < -1) {
      return `${baseClasses} opacity-0 scale-[0.7] -translate-z-[200px] z-0`;
    } else {
      return `${baseClasses} opacity-100 scale-100 translate-z-0 z-[2]`;
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isAnimating || duplicatedSlides.length <= 1) return;

    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    setDragStartTime(Date.now());
    setSwipeExecuted(false);

    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  };

  const processSwipe = useCallback(() => {
    const deltaX = currentX - startX;

    const minSwipeDistance = 10;

    const shouldTriggerSwipe =
      Math.abs(deltaX) > minSwipeDistance || Math.abs(deltaX) > 15;

    if (shouldTriggerSwipe) {
      setSwipeExecuted(true);

      if (deltaX > 0) {
        handlePrev();
      } else {
        handleNext();
      }
    }
  }, [currentX, startX, dragStartTime, handlePrev, handleNext]);

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX;
      setCurrentX(newX);

      const deltaX = Math.abs(newX - startX);
      if (deltaX > 3 && !blockAllInteractions) {
        setBlockAllInteractions(true);
      }
    };

    const handleGlobalMouseUp = () => {
      if (!isDragging) return;
      setIsDragging(false);

      const deltaX = Math.abs(currentX - startX);

      if (deltaX <= 3) {
        setBlockAllInteractions(false);
        setSwipeExecuted(false);
        return;
      }

      processSwipe();

      setTimeout(() => {
        setSwipeExecuted(false);
        setBlockAllInteractions(false);
      }, 200);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.userSelect = 'none';
    }

    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('click', handleGlobalClick, true);
      document.body.style.userSelect = '';
    };
  }, [
    isDragging,
    processSwipe,
    blockAllInteractions,
    swipeExecuted,
    startX,
    currentX,
  ]);

  const handleClick = (e: React.MouseEvent) => {
    if (blockAllInteractions || swipeExecuted) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();

      return false;
    }
  };

  const handleGlobalClick = (e: Event) => {
    const target = e.target as Element;
    const sliderElement = sliderRef.current;

    if (!sliderElement || !target) return;

    const isInsideSlider = sliderElement.contains(target);

    if (isInsideSlider && (blockAllInteractions || swipeExecuted)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
  };

  return (
    <div className={className}>
      <div className="flex h-full w-full">
        <div className="w-[368px] min-w-[368px]">{leftContent}</div>

        <div
          className="relative ml-8 min-w-[calc(100%-388px)] overflow-hidden"
          style={{ height: slideHeight }}
        >
          <div
            className={classNames({
              cls: 'relative h-full w-full overflow-visible [perspective-origin:left_center] [perspective:1000px] [transform-style:preserve-3d]',
              mods: {
                'overflow-hidden': isTransitioningToFirst,
                'pointer-events-none': blockAllInteractions,
              },
            })}
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            style={{
              userSelect: 'none',
              pointerEvents: blockAllInteractions ? 'none' : 'auto',
            }}
          >
            {duplicatedSlides.map((slide, index) => (
              <div
                key={index}
                className={getSlideClasses(index)}
                style={{
                  ...getSlidePosition(index),
                  backfaceVisibility: 'hidden',
                  transformStyle: 'preserve-3d',
                  width: slideWidth,
                  pointerEvents: blockAllInteractions ? 'none' : 'auto',
                }}
                onClick={handleClick}
              >
                <div
                  className="h-full"
                  ref={(el) => {
                    slideRefs.current[index] = el;
                  }}
                  style={{
                    width: slideWidth,
                    minWidth: slideWidth,
                    maxWidth: slideWidth,
                  }}
                >
                  {slide}
                </div>
              </div>
            ))}
          </div>

          <img
            className="pointer-events-none absolute top-[-140px] right-[-85px] z-10 h-[calc(100%+280px)] w-[293px]"
            src={overlayUrl}
            alt="background"
          />
        </div>
      </div>

      {!isSingleSlide && (
        <div className="ml-[136px]">
          <SliderPaginationButtons
            handleNext={handleNext}
            handlePrev={handlePrev}
            disabled={isAnimating}
          />
        </div>
      )}
    </div>
  );
};

function getDuplicatedSlides(slides: React.ReactNode[]): React.ReactNode[] {
  if (slides.length <= 1) return slides;

  let duplicatedSlides = [...slides, ...slides];

  while (duplicatedSlides.length < 10) {
    duplicatedSlides = [...duplicatedSlides, ...slides];
  }

  return duplicatedSlides;
}
